import React from 'react';
import Modal from '../../../components/Modal/Modal';
import ModalHeader from '../../../components/Modal/ModalHeader';
import ModalBody from '../../../components/Modal/ModalBody';
import ModalFooter from '../../../components/Modal/ModalFooter';
import Checkbox from '../../../components/Checkbox/Checkbox';
import { Radio } from '../../../components/RadioGroup/RadioGroup';
import MenuSyncMode from '../../types/MenuSyncMode.enum';
import MenuSyncOptions from '../../types/MenuSyncOptions.interface';
import styles from './SyncMenuItemsModal.module.scss';

export interface SyncMenuItemsModalProps {
  setShowModal: (visible: boolean) => void;
  onSync: (syncOptions: MenuSyncOptions) => void;
}

interface SyncModeOption {
  value: MenuSyncMode;
  label: string;
  description: string;
}

// The lighter option is listed (and selected) first: it is the one that is safe to run any time, while a
// full import can add a large number of items to the menu.
const SYNC_MODE_OPTIONS: SyncModeOption[] = [
  {
    value: MenuSyncMode.AVAILABILITY,
    label: 'Availability & Pricing Only',
    description:
      'Updates prices and stock availability on the items and mods you already have. Nothing new is added. This is the quicker option and is safe to run any time.',
  },
  {
    value: MenuSyncMode.FULL,
    label: 'Full Menu Item Import',
    description:
      'Everything above, plus a new item or mod is created for every POS item that does not have one yet. Use this after adding items to your POS. Large menus can take several minutes.',
  },
];

const SyncMenuItemsModal = (props: SyncMenuItemsModalProps): JSX.Element => {
  const { setShowModal, onSync } = props;

  const [mode, setMode] = React.useState<MenuSyncMode>(SYNC_MODE_OPTIONS[0].value);
  // Off by default: the first location promoted is what defines the shared item names for the whole
  // company, so adding to the shared library stays a deliberate choice.
  const [promoteToTemplates, setPromoteToTemplates] = React.useState(false);

  const isFullImport = mode === MenuSyncMode.FULL;

  return (
    <Modal setShowModal={setShowModal}>
      <ModalHeader headerLabel='Sync With POS' setShowModal={setShowModal} showDismissIcon />
      <ModalBody>
        <div className={styles.intro}>Choose how much of the POS menu you want to bring over.</div>
        <div className='radio-group'>
          {SYNC_MODE_OPTIONS.map((option) => (
            <div key={option.value} className={styles.option}>
              <Radio
                groupName='syncMode'
                option={{ label: option.label, value: option.value }}
                selectedValue={mode}
                onChange={() => setMode(option.value)}
              />
              <div className={styles.description}>{option.description}</div>
              {option.value === MenuSyncMode.FULL && (
                <div className={styles.subOption}>
                  <Checkbox
                    label='Also add these to Shared Menu Items'
                    additionalHelp={
                      'New shared items are created only for names that are not already there — anything ' +
                      'that matches an existing shared item links to it instead.'
                    }
                    checked={isFullImport && promoteToTemplates}
                    disabled={!isFullImport}
                    onChange={() => setPromoteToTemplates(!promoteToTemplates)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </ModalBody>
      <ModalFooter
        primaryLabel='Sync'
        primaryActionHandler={() => {
          setShowModal(false);
          // promoteToTemplates is meaningless outside a full import, so never send it set on its own.
          onSync({ mode, promoteToTemplates: isFullImport && promoteToTemplates });
        }}
        secondaryLabel='Cancel'
        secondaryActionHandler={() => setShowModal(false)}
      />
    </Modal>
  );
};

export default SyncMenuItemsModal;
